import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Container,
  Form,
  FormField,
  Group,
  Link,
  SectionBannerContent,
  SectionBannerDescription,
  SectionBannerIcon,
  SectionBannerRoot,
  Text,
  useMelioForm,
} from '@melio/penny';
import { object, ref, string } from 'yup';

import { resetPassword } from '../../api/auth';
import { useAnalytics } from '../../analytics';
import { getEntryPoint } from '../../session';
import { sanitizeAuthorizeRedirect } from '../../utils/sanitizeRedirectUrl';
import { PasswordField, isPasswordValid } from '../PasswordField/PasswordField';

// The reset link opens in a new tab that lost the login redirect. Pass the one
// auth-service returned to sign-in, which stores it and continues there after login.
const buildPostResetSignInUrl = (redirect: string | null) => {
  const queryParams = new URLSearchParams({ reset: 'success' });
  if (redirect) {
    queryParams.set('redirect', redirect);
  }
  return `/sign-in?${queryParams.toString()}`;
};

interface ResetPasswordFormModel {
  password: string;
  confirmPassword: string;
}

const useResetPasswordValidationSchema = () => {
  return object().shape({
    password: string().defined(),
    confirmPassword: string()
      .oneOf([ref('password')], "Passwords don't match.")
      .required('Please confirm your password.'),
  });
};

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { track } = useAnalytics();

  const token = searchParams.get('token') ?? '';

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLinkInvalid, setIsLinkInvalid] = useState(!token);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const schema = useResetPasswordValidationSchema();

  const trackClick = () => {
    track({
      eventName: 'Connection-Click',
      properties: {
        PageName: 'set-new-password',
        Flow: 'sign-in',
        Intent: 'reset-password',
        EntryPoint: getEntryPoint(),
        Cta: 'save-new-password',
        NewPasswordInteraction: !!formState.touchedFields.password,
        ConfirmPasswordInteraction: !!formState.touchedFields.confirmPassword,
      },
    });
  };

  const trackStatus = (statusType: 'success' | 'failure', errorType?: 'invalid-password' | 'password-not-confirmed') => {
    track({
      eventName: 'Connection-Status',
      properties: {
        PageName: 'set-new-password',
        Flow: 'sign-in',
        Intent: 'reset-password',
        EntryPoint: getEntryPoint(),
        Cta: 'save-new-password',
        StatusType: statusType,
        ...(errorType && { ErrorType: errorType }),
      },
    });
  };

  useEffect(() => {
    track({
      eventName: 'Connection-View',
      properties: {
        PageName: 'set-new-password',
        Flow: 'sign-in',
        Intent: 'reset-password',
        EntryPoint: getEntryPoint(),
      },
    });
  }, []);

  const { formProps, registerField, handleSubmit, formState } = useMelioForm<ResetPasswordFormModel>({
    onSubmit: async (data) => {
      setHasAttemptedSubmit(true);

      if (!isPasswordValid(data.password)) {
        trackStatus('failure', 'invalid-password');
        return;
      }
      if (data.password !== data.confirmPassword) {
        trackStatus('failure', 'password-not-confirmed');
        return;
      }

      setIsLoading(true);
      setError(null);
      trackClick();

      const result = await resetPassword({ token, password: data.password });

      if (result.type === 'expired') {
        trackStatus('failure');
        setIsLinkInvalid(true);
        setIsLoading(false);
        return;
      }

      if (result.type === 'error') {
        trackStatus('failure');
        setError(result.message);
        setIsLoading(false);
        return;
      }

      trackStatus('success');
      navigate(buildPostResetSignInUrl(sanitizeAuthorizeRedirect(result.redirect)));
    },
    schema,
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = handleSubmit();

  const handleFormKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  };

  if (isLinkInvalid) {
    return (
      <Container height="full" paddingX="s" paddingTop="xxl" data-testid="reset-password-page">
        <Container overflow="visible" style={{ width: '100%', maxWidth: '480px' }}>
          <Group variant="vertical" spacing="l" alignItems="stretch">
            <SectionBannerRoot variant="critical" data-testid="reset-password-invalid-banner">
              <SectionBannerIcon />
              <SectionBannerContent>
                <SectionBannerDescription>This password reset link is invalid or has expired.</SectionBannerDescription>
              </SectionBannerContent>
            </SectionBannerRoot>
            <Group variant="horizontal" justifyContent="center">
              <Link
                href="#"
                variant="standalone"
                size="medium"
                label="Request a new link"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  navigate('/forgot-password');
                }}
                data-testid="request-new-link"
              />
            </Group>
          </Group>
        </Container>
      </Container>
    );
  }

  return (
    <Container height="full" paddingX="s" paddingTop="xxl" data-testid="reset-password-page">
      <Container overflow="visible" style={{ width: '100%', maxWidth: '480px' }}>
        <Group variant="vertical" spacing="l" alignItems="stretch">
          {error && (
            <SectionBannerRoot variant="critical" data-testid="reset-password-error-message">
              <SectionBannerIcon />
              <SectionBannerContent>
                <SectionBannerDescription>{error}</SectionBannerDescription>
              </SectionBannerContent>
            </SectionBannerRoot>
          )}

          <Text textStyle="heading1Semi" color="semantic.text.primary" data-testid="reset-password-page-title">
            Set a new password
          </Text>

          <Form {...formProps} onKeyDown={handleFormKeyDown} columns={1} autoComplete="on" data-testid="reset-password-form">
            <PasswordField
              label="New password"
              fieldProps={registerField('password')}
              testId="password-input"
              showInvalidIcon={hasAttemptedSubmit}
              autoComplete="new-password"
            />

            <PasswordField
              label="Confirm password"
              fieldProps={registerField('confirmPassword')}
              testId="confirm-password-input"
              showRules={false}
              showInvalidIcon={false}
              autoComplete="new-password"
            />

            <FormField
              render={() => (
                <Button
                  label="Save password"
                  variant="primary"
                  size="large"
                  isFullWidth
                  isLoading={isLoading}
                  onClick={() => onSubmit()}
                  data-testid="save-new-password-button"
                />
              )}
            />
          </Form>
        </Group>
      </Container>
    </Container>
  );
};
